import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserRole } from "../../api/auth/types";

export interface UserState {
  id: number | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  departmentId: number | null;
  channelId: number | null;
  role: UserRole | null;
  accessToken: string | null;
  refreshToken: string | null;
}

const initialState: UserState = {
  id: null,
  username: null,
  firstName: null,
  lastName: null,
  departmentId: null,
  channelId: null,
  role: null,
  accessToken: null,
  refreshToken: null,
};

export interface SetUserPayload {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  department_id: number | null;
  channel_id: number | null;
  role: UserRole;
  access_token: string;
  refresh_token: string;
}

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<SetUserPayload>) => {
      const payload = action.payload;
      state.id = payload.id ?? null;
      state.username = payload.username ?? null;
      state.firstName = payload.first_name ?? null;
      state.lastName = payload.last_name ?? null;
      state.departmentId = payload.department_id ?? null;
      state.channelId = payload.channel_id ?? null;
      state.role = payload.role ?? null;
      state.accessToken = payload.access_token ?? null;
      state.refreshToken = payload.refresh_token ?? null;
    },
    setPartialUser: (state, action: PayloadAction<Partial<SetUserPayload>>) => {
      const payload = action.payload;
      if (payload.id !== undefined) state.id = payload.id as number;
      if (payload.username !== undefined) state.username = payload.username as string;
      if (payload.first_name !== undefined) state.firstName = payload.first_name as string;
      if (payload.last_name !== undefined) state.lastName = payload.last_name as string;
      if (payload.department_id !== undefined) state.departmentId = payload.department_id as number | null;
      if (payload.channel_id !== undefined) state.channelId = payload.channel_id as number | null;
      if (payload.role !== undefined) state.role = payload.role as UserRole;
      if (payload.access_token !== undefined) state.accessToken = payload.access_token as string;
      if (payload.refresh_token !== undefined) state.refreshToken = payload.refresh_token as string;
    },
    clearUser: (state) => {
      state.id = null;
      state.username = null;
      state.firstName = null;
      state.lastName = null;
      state.departmentId = null;
      state.channelId = null;
      state.role = null;
      state.accessToken = null;
      state.refreshToken = null;
    },
    setRole: (state, action: PayloadAction<UserRole | null>) => {
      state.role = action.payload;
    },
  },
});

export const { setUser, setPartialUser, clearUser, setRole } = userSlice.actions;
export const userReducer = userSlice.reducer;

// Selectors
export const selectUser = (state: { user: UserState }) => state.user;
export const selectUserRole = (state: { user: UserState }) => state.user.role;
export const selectUserId = (state: { user: UserState }) => state.user.id;
export const selectUserDepartmentId = (state: { user: UserState }) => state.user.departmentId;
export const selectUserChannelId = (state: { user: UserState }) => state.user.channelId;
